import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import BackButton from "../Utilities/BackButton";
import { BASE_URL } from "../../Constants/constants";
import { FaUserAlt, FaPassport, FaBuilding, FaPhoneSquareAlt, FaMapMarkedAlt } from 'react-icons/fa';

const UserDetail = () => {
    const { t } = useTranslation('userManagement');
    const { userId } = useParams();
    const [userDetails, setUserDetails] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchUserDetails(userId);
    }, [userId]);

    const fetchUserDetails = async (id) => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            console.error("No token found, please login.");
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/staff/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                setErrorMessage(t('userManagement.userDetail.notStartedCheckIn'));
                setUserDetails(null);
                return;
            }

            const data = await response.json();
            if (data && Object.keys(data).length === 0) {
                setUserDetails(null);
                setErrorMessage(t('userManagement.userDetail.notStartedCheckIn'));
            } else {
                setUserDetails(data);
            }
        } catch (error) {
            console.error('Error fetching user details: ', error);
            setErrorMessage(t('userManagement.userDetail.errorRetrieving'));
            setUserDetails(null);
        }
    };

    return (
        <Container className="mt-4">
            <Row className="justify-content-md-center">
                <Col xs={12}>
                    <h2 className="text-center mb-4">{t('userManagement.userDetail.title')}</h2>
                    {userDetails ? (
                        <>
                            <Row>
                                {/* Personal Information Section */}
                                <Col md={6}>
                                    <Card border="light" className="mb-3">
                                        <Card.Body className="text-center">
                                            <FaUserAlt size={30} className="mb-2" />
                                            <Card.Title>{t('userManagement.userDetail.personalInfo')}</Card.Title>
                                            <Card.Text>
                                                {t('userManagement.userDetail.id')}: {userDetails.id}<br />
                                                {t('userManagement.userDetail.name')}: {userDetails.user.first_name} {userDetails.user.last_name}<br />
                                                {t('userManagement.form.email')}: {userDetails.user.email}<br />
                                                {t('userManagement.userDetail.gender')}: {userDetails.gender}<br />
                                                {t('userManagement.userDetail.nationality')}: {userDetails.nationality}<br />
                                                {t('userManagement.userDetail.dateOfBirth')}: {userDetails.date_of_birth}<br />
                                                {t('userManagement.userDetail.addressOutsideHaiti')}: {userDetails.address_outside_haiti}<br />
                                                {t('userManagement.userDetail.dependants')}: {userDetails.dependants}
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Identification Documents Section */}
                                <Col md={6}>
                                    <Card border="light" className="mb-3">
                                        <Card.Body className="text-center">
                                            <FaPassport size={30} className="mb-2" />
                                            <Card.Title>{t('userManagement.userDetail.identification')}</Card.Title>
                                            <Card.Text>
                                                {t('userManagement.userDetail.unId')}: {userDetails.un_id} ({t('userManagement.userDetail.expiration')}: {userDetails.un_id_expiration_date})<br />
                                                {t('userManagement.userDetail.unlp')}: {userDetails.identification_documents.unlp}<br />
                                                {t('userManagement.userDetail.nationalPassport')}: {userDetails.identification_documents.national_passport}
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            <Row>
                                {/* Employment Details Section */}
                                <Col md={6}>
                                    <Card border="light" className="mb-3">
                                        <Card.Body className="text-center">
                                            <FaBuilding size={30} className="mb-2" />
                                            <Card.Title>{t('userManagement.userDetail.employment')}</Card.Title>
                                            <Card.Text>
                                                {t('userManagement.userDetail.function')}: {userDetails.employment.function}<br />
                                                {t('userManagement.userDetail.section')}: {userDetails.employment.section}<br />
                                                {t('userManagement.userDetail.unit')}: {userDetails.employment.unit}<br />
                                                {t('userManagement.userDetail.dutyStation')}: {userDetails.employment.duty_station}<br />
                                                {t('userManagement.userDetail.typeOfContract')}: {userDetails.employment.type_of_contract} ({t('userManagement.userDetail.expiration')}: {userDetails.employment.expiration_date})<br />
                                                {t('userManagement.userDetail.supervisor')}: {userDetails.employment.supervisor}<br />
                                                {t('userManagement.userDetail.modeOfWork')}: {userDetails.employment.mode_of_work}
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Contact Information Section */}
                                <Col md={6}>
                                    <Card border="light" className="mb-3">
                                        <Card.Body className="text-center">
                                            <FaPhoneSquareAlt size={30} className="mb-2" />
                                            <Card.Title>{t('userManagement.userDetail.contact')}</Card.Title>
                                            <Card.Text>
                                                {t('userManagement.userDetail.callSign')}: {userDetails.contacts.call_sign}<br />
                                                {t('userManagement.userDetail.officePhone')}: {userDetails.contacts.office_phone}<br />
                                                {t('userManagement.userDetail.dectPhone')}: {userDetails.contacts.dect_phone}<br />
                                                {t('userManagement.userDetail.mobilePhone')}: {userDetails.contacts.mobile_phone}<br />
                                                {t('userManagement.userDetail.satNumber')}: {userDetails.contacts.sat_number}<br />
                                                {t('userManagement.userDetail.emergencyContact')}: {userDetails.contacts.emergency_contact}<br />
                                                {t('userManagement.userDetail.proofOfLife')}: {userDetails.contacts.proof_of_life}
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Address in Haiti Section */}
                            <Row>
                                <Col md={12}>
                                    <Card border="light" className="mb-3">
                                        <Card.Body className="text-center">
                                            <FaMapMarkedAlt size={30} className="mb-2" />
                                            <Card.Title>{t('userManagement.userDetail.addressHaiti')}</Card.Title>
                                            <Card.Text>
                                                {t('userManagement.userDetail.address')}: {userDetails.addresses_haiti.address_in_haiti}<br />
                                                {t('userManagement.userDetail.gps')}: {userDetails.addresses_haiti.gps_latitude}, {userDetails.addresses_haiti.gps_longitude}<br />
                                                {t('userManagement.userDetail.wardenZone')}: {userDetails.addresses_haiti.warden_zone}
                                            </Card.Text>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        errorMessage ? (
                            <Alert variant="info">{errorMessage}</Alert>
                        ) : (
                            <p>{t('userManagement.messages.loading')}</p>
                        )
                    )}
                    <BackButton url="/users" labelText={t('userManagement.userDetail.backToUserManagement')}/>
                </Col>
            </Row>
        </Container>
    );
};

export default UserDetail;